const COURSE_SECTION_PATTERN = /^##\s*课程(?:[:：]\s*(.+))?$/m

const safeTrim = (value) => String(value || '').trim()

const getSourceConfig = (merchantConfig = {}) => merchantConfig.courseSource || null

export const hasMarkdownCourseSource = (merchantConfig = {}) => {
  const source = getSourceConfig(merchantConfig)
  return !!(source && source.type === 'markdown' && safeTrim(source.markdown))
}

const getMarkdownContent = (merchantConfig = {}) => {
  const source = getSourceConfig(merchantConfig)
  if (!source || source.type !== 'markdown') return ''
  return safeTrim(source.markdown)
}

const parseDurationToSeconds = (durationText) => {
  const text = safeTrim(durationText)
  if (!text) return 0
  const parts = text.split(':').map(part => Number(part))
  if (parts.some(part => Number.isNaN(part))) return 0
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1]
  }
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2]
  }
  return Number(text) || 0
}

const buildCourseIntroHtml = (briefText, imageUrls = []) => {
  const briefHtml = briefText ? `<p>${briefText}</p>` : ''
  const imageHtml = imageUrls
    .filter(Boolean)
    .map(url => `<p><img src="${url}" alt="课程详情图" /></p>`)
    .join('')
  return `${briefHtml}${imageHtml}`
}

const parseCourseSection = (sectionText, merchantKey, courseIndex) => {
  const sectionLines = sectionText.split('\n').map(line => line.trim()).filter(Boolean)
  if (!sectionLines.length) return null

  let title = ''
  let tags = []
  let briefText = ''
  const detailImages = []
  const courseVideoList = []
  let currentMode = ''

  sectionLines.forEach((line) => {
    if (/^课程名称[:：]/.test(line)) {
      title = safeTrim(line.replace(/^课程名称[:：]/, ''))
      currentMode = ''
      return
    }
    if (/^标签[:：]/.test(line)) {
      tags = safeTrim(line.replace(/^标签[:：]/, '')).split(/[，,]/).map(item => safeTrim(item)).filter(Boolean)
      currentMode = ''
      return
    }
    if (/^简介[:：]/.test(line)) {
      briefText = safeTrim(line.replace(/^简介[:：]/, ''))
      currentMode = ''
      return
    }
    if (/^详情图片[:：]?$/.test(line)) {
      currentMode = 'images'
      return
    }
    if (/^课程视频列表[:：]?$/.test(line)) {
      currentMode = 'videos'
      return
    }

    if (currentMode === 'images' && /^-\s+/.test(line)) {
      detailImages.push(safeTrim(line.replace(/^-\s+/, '')))
      return
    }

    if (currentMode === 'videos' && /^-\s+/.test(line)) {
      const rawVideoLine = safeTrim(line.replace(/^-\s+/, ''))
      const [videoTitle, durationText, videoUrl] = rawVideoLine.split('|').map(item => safeTrim(item))
      if (videoTitle && videoUrl) {
        courseVideoList.push({
          videoId: `${merchantKey}-course-${courseIndex + 1}-video-${courseVideoList.length + 1}`,
          title: videoTitle,
          duration: parseDurationToSeconds(durationText),
          durationText,
          videoUrl,
          coverUrl: detailImages[0] || ''
        })
      }
    }
  })

  if (!title) return null

  return {
    id: `${merchantKey}-course-${courseIndex + 1}`,
    merchant: merchantKey,
    merchantCode: merchantKey,
    title,
    type: tags[0] || '定制课程',
    tags,
    briefText,
    intro: buildCourseIntroHtml(briefText, detailImages),
    coverUrl: detailImages[0] || '',
    detailImages,
    vip: false,
    courseVideoList,
    totalDuration: courseVideoList.reduce((sum, item) => sum + (item.duration || 0), 0)
  }
}

export const getMerchantCoursesFromMarkdown = (merchantConfig = {}) => {
  const markdown = getMarkdownContent(merchantConfig)
  if (!markdown) return []

  const sections = markdown
    .split(/^##\s*课程(?:[:：].*)?$/m)
    .slice(1)

  return sections
    .map((section, index) => parseCourseSection(section, merchantConfig.key || 'merchant', index))
    .filter(Boolean)
}

const getHistoryStorageKey = (merchantKey) => `rehab-merchant-course-history:${merchantKey}`

const readMerchantHistoryMap = (merchantKey) => {
  if (typeof window === 'undefined' || !window.localStorage) return {}
  try {
    return JSON.parse(window.localStorage.getItem(getHistoryStorageKey(merchantKey)) || '{}')
  } catch (error) {
    return {}
  }
}

const writeMerchantHistoryMap = (merchantKey, data) => {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.setItem(getHistoryStorageKey(merchantKey), JSON.stringify(data || {}))
}

export const listMerchantCourseHistory = (merchantConfig = {}) => {
  const courseList = getMerchantCoursesFromMarkdown(merchantConfig)
  const historyMap = readMerchantHistoryMap(merchantConfig.key || 'merchant')

  return courseList
    .filter(course => historyMap[course.id])
    .map(course => {
      const history = historyMap[course.id]
      return {
        ...course,
        cover: course.coverUrl,
        lastWatchTime: history.lastWatchTime,
        lastWatchDuration: history.lastWatchDuration,
        totalPlayedSec: history.totalPlayedSec,
        progress: history.progress,
        courseId: course.id
      }
    })
}

export const saveMerchantCourseHistory = ({ merchantConfig = {}, course, videoId, endPosition = 0 }) => {
  if (!merchantConfig.key || !course || !course.id) return

  const historyMap = readMerchantHistoryMap(merchantConfig.key)
  const history = historyMap[course.id] || {
    videoProgress: {}
  }

  const currentPosition = Math.max(Number(endPosition) || 0, Number(history.videoProgress && history.videoProgress[videoId]) || 0)
  history.videoProgress = {
    ...(history.videoProgress || {}),
    [videoId]: currentPosition
  }
  history.lastWatchTime = Date.now()
  history.lastWatchDuration = currentPosition
  history.totalPlayedSec = Object.values(history.videoProgress).reduce((sum, item) => sum + (Number(item) || 0), 0)
  history.progress = course.totalDuration > 0 ? Math.min(100, Math.floor((history.totalPlayedSec / course.totalDuration) * 100)) : 0

  historyMap[course.id] = history
  writeMerchantHistoryMap(merchantConfig.key, historyMap)
}
